import type {
  MetricExtractionSpec,
  MetricKind,
  QuestionnaireValidationIssue,
  QuestionType,
  RatingPolarity,
} from "./index";
import { hasUsableNonImageAssetMediaLocator } from "./asset-media-locator";
import { brandRoster, isRosterEntryOf, type BrandRosterEntry } from "./brand-roster";
import { openEndClassifierMetricId } from "./open-end-classifier";
import {
  RATING_POLARITIES,
  getUnitRegistryEntry,
  isContinuousUnitId,
} from "./registry";
import { issueSeverity, repairLine, worded } from "./rule-catalog";
import { ruleImpl } from "./rule-impl";
import { MATRIX_QUESTION_TYPES } from "./question-capabilities";
import type { RuleCode } from "./rule-codes.generated";
import { rankingCountIssues, rankingCountRange } from "./validator";
import { isWholeNumberUnit } from "./vocabularies";
import { keyIdentity } from "./identity";

type JsonRecord = Record<string, unknown>;

type MetricCapability = Readonly<{
  sourceKind: "question" | "matrix_measure";
  method: MetricExtractionSpec["method"];
  metricKind: MetricKind;
  unit: "owner" | "absent";
}>;

/**
 * The metric tuples the current questionnaire builder actually produces and
 * canonical analysis can execute. The wider persisted vocabulary remains in
 * the structural contract for historical reads; plan-bearing accepted state
 * must select one of these current executable tuples.
 */
export const CURRENT_GENERATED_METRIC_CAPABILITIES = {
  single_select: [
    {
      sourceKind: "question",
      method: "distribution",
      metricKind: "distribution",
      unit: "absent",
    },
  ],
  multi_select: [
    {
      sourceKind: "question",
      method: "multi_select_share",
      metricKind: "share",
      unit: "absent",
    },
  ],
  rating: [
    {
      sourceKind: "question",
      method: "rating_secondary_metrics",
      metricKind: "mean",
      unit: "owner",
    },
    {
      sourceKind: "question",
      method: "paired_lift",
      metricKind: "mean",
      unit: "owner",
    },
  ],
  continuous: [
    {
      sourceKind: "question",
      method: "continuous_summary",
      metricKind: "mean",
      unit: "owner",
    },
  ],
  open_end: [],
  matrix_rating: [
    {
      sourceKind: "matrix_measure",
      method: "matrix_rating_summary",
      metricKind: "mean",
      unit: "owner",
    },
    {
      sourceKind: "matrix_measure",
      method: "paired_lift",
      metricKind: "mean",
      unit: "owner",
    },
  ],
  matrix_categorical: [
    {
      sourceKind: "question",
      method: "matrix_categorical_distribution",
      metricKind: "distribution",
      unit: "absent",
    },
  ],
  ranking: [
    {
      sourceKind: "question",
      method: "ranking_weighted_score",
      metricKind: "ranking_weighted_score",
      unit: "absent",
    },
  ],
} as const satisfies Readonly<
  Record<QuestionType, ReadonlyArray<MetricCapability>>
>;

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);
const records = (value: unknown): Array<JsonRecord> =>
  Array.isArray(value) ? value.filter(isRecord) : [];
const nonBlank = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

function issue(
  issues: Array<QuestionnaireValidationIssue>,
  path: string,
  code: string,
  message: string,
): void {
  issues.push({ path, code, message, severity: issueSeverity(code) });
}

function duplicateValueIssues(
  issues: Array<QuestionnaireValidationIssue>,
  entries: ReadonlyArray<JsonRecord>,
  field: string,
  path: string,
): void {
  const firstByValue = new Map<string, number>();
  entries.forEach((entry, index) => {
    const value = entry[field];
    if (!nonBlank(value)) return;
    // Compared case aside, as the key rule compares every key and value.
    const firstIndex = firstByValue.get(keyIdentity(value));
    if (firstIndex !== undefined) {
      issue(
        issues,
        `${path}[${index}].${field}`,
        "accepted_identity_duplicate",
        repairLine("accepted_identity_duplicate", { field: String(field), pathValue: String(path), firstIndex: String(firstIndex) }),
      );
      return;
    }
    firstByValue.set(keyIdentity(value), index);
  });
}

function responseIdentityIssues(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonRecord,
  questionIndex: number,
): void {
  if (!isRecord(question.response)) return;
  const response = question.response;
  const responsePath = `researchQuestions[${questionIndex}].response`;

  const choices =
    response.kind === "matrix_categorical"
      ? records(response.categories)
      : records(response.options);
  const choiceField =
    response.kind === "matrix_categorical" ? "categories" : "options";
  if (choices.length > 0) {
    duplicateValueIssues(
      issues,
      choices,
      "id",
      `${responsePath}.${choiceField}`,
    );
    duplicateValueIssues(
      issues,
      choices,
      "value",
      `${responsePath}.${choiceField}`,
    );
  }

  const rows = isRecord(response.rows) ? response.rows : null;
  if (rows?.source === "fixed") {
    const fixedRows = records(rows.rows);
    duplicateValueIssues(issues, fixedRows, "id", `${responsePath}.rows.rows`);
    duplicateValueIssues(
      issues,
      fixedRows,
      "value",
      `${responsePath}.rows.rows`,
    );
  }

  if (response.kind === "matrix_rating") {
    duplicateValueIssues(
      issues,
      records(response.measures),
      "id",
      `${responsePath}.measures`,
    );
  }
}

/**
 * An open end follows up on earlier questions only: never on itself, a later
 * question or one that is gone (the order a respondent answers in). The
 * accepted-state gate and the editor's live check (L3) read this one check.
 */
export function followUpRelationIssues(
  draft: JsonRecord,
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  const earlierIds = new Set<string>();
  records(draft.researchQuestions).forEach((question, questionIndex) => {
    const response = isRecord(question.response) ? question.response : {};
    const parentIds = Array.isArray(response.followUpToQuestionIds)
      ? response.followUpToQuestionIds
      : [];
    parentIds.forEach((parentId, parentIndex) => {
      if (typeof parentId === "string" && earlierIds.has(parentId)) return;
      issue(
        issues,
        `researchQuestions[${questionIndex}].response.followUpToQuestionIds[${parentIndex}]`,
        "accepted_follow_up_parent_invalid",
        repairLine("accepted_follow_up_parent_invalid"),
      );
    });
    if (nonBlank(question.id)) earlierIds.add(question.id);
  });
  return issues;
}

/**
 * Each question's answer bounds against its own answers: a multi-select's
 * and a ranking's counts (`accepted_response_cardinality_invalid`), a
 * number's range (`continuous_bounds_inverted`, `continuous_bounds_not_registry`).
 * The accepted-state gate and the editor's live check (E-questions-07) read
 * this one check.
 */
export function responseRelationIssues(
  draft: JsonRecord,
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  records(draft.researchQuestions).forEach((question, questionIndex) => {
    if (!isRecord(question.response)) return;
    const response = question.response;
    const responsePath = `researchQuestions[${questionIndex}].response`;
    const optionCount = records(response.options).length;

    if (response.kind === "multi_select") {
      const min = response.minSelected;
      const max = response.maxSelected;
      if (
        typeof min === "number" &&
        (!Number.isInteger(min) || min < 0 || min > optionCount)
      ) {
        issue(
          issues,
          `${responsePath}.minSelected`,
          "accepted_response_cardinality_invalid",
          worded("accepted_response_cardinality_invalid", "minSelected must be a whole number between 0 and the option count."),
        );
      }
      if (
        typeof max === "number" &&
        (!Number.isInteger(max) || max < 0 || max > optionCount)
      ) {
        issue(
          issues,
          `${responsePath}.maxSelected`,
          "accepted_response_cardinality_invalid",
          worded("accepted_response_cardinality_invalid", "maxSelected must be a whole number between 0 and the option count."),
        );
      }
      if (typeof min === "number" && typeof max === "number" && min > max) {
        issue(
          issues,
          `${responsePath}.minSelected`,
          "accepted_response_cardinality_invalid",
          worded("accepted_response_cardinality_invalid", "minSelected must not exceed maxSelected."),
        );
      }
      if (
        records(response.options).some((option) => option.exclusive === true) &&
        typeof min === "number" &&
        min > 1
      ) {
        issue(
          issues,
          `${responsePath}.minSelected`,
          "accepted_response_cardinality_invalid",
          worded("accepted_response_cardinality_invalid", "An exclusive option requires minSelected to be at most 1."),
        );
      }
    }

    if (response.kind === "ranking") {
      const { floor } = rankingCountRange(response);
      for (const { field, rule } of rankingCountIssues(response)) {
        issue(
          issues,
          `${responsePath}.${field}`,
          "accepted_response_cardinality_invalid",
          worded(
            "accepted_response_cardinality_invalid",
            rule === "order"
              ? "minRankedItems must not exceed maxRankedItems."
              : `${field} must be a whole number between ${floor} and the option count.`,
          ),
        );
      }
    }

    if (response.kind !== "continuous") return;
    if (
      typeof response.min === "number" &&
      typeof response.max === "number" &&
      response.min > response.max
    ) {
      issue(
        issues,
        `${responsePath}.min`,
        "continuous_bounds_inverted",
        repairLine("continuous_bounds_inverted"),
      );
    }

    const unit = response.unit;
    if (typeof unit !== "string" || !isContinuousUnitId(unit)) return;
    const registry = getUnitRegistryEntry(unit);
    if (
      typeof response.min === "number" &&
      typeof registry?.rawMin === "number" &&
      response.min !== registry.rawMin
    ) {
      issue(
        issues,
        `${responsePath}.min`,
        "continuous_bounds_not_registry",
        worded("continuous_bounds_not_registry", `Authored min for ${unit} must equal its registry bound ${registry.rawMin}.`),
      );
    }
    if (
      typeof response.max === "number" &&
      typeof registry?.rawMax === "number" &&
      response.max !== registry.rawMax
    ) {
      issue(
        issues,
        `${responsePath}.max`,
        "continuous_bounds_not_registry",
        worded("continuous_bounds_not_registry", `Authored max for ${unit} must equal its registry bound ${registry.rawMax}.`),
      );
    }
    // A whole-number unit (a count) is answered in whole numbers, 0 or more,
    // so its bounds are too.
    if (isWholeNumberUnit(unit)) {
      for (const bound of ["min", "max"] as const) {
        const value = response[bound];
        if (typeof value === "number" && (!Number.isInteger(value) || value < 0)) {
          issue(
            issues,
            `${responsePath}.${bound}`,
            "continuous_bounds_not_registry",
            worded("continuous_bounds_not_registry", `Authored ${bound} for ${unit} must be a whole number, 0 or more.`),
          );
        }
      }
    }

    // A currencyCode (non-blank, currency only) and a durationUnit (duration
    // only) are the normaliser's to write (`normalizeResponse`: the registry
    // default currency, a duration's own unit, neither on any other unit),
    // and every write that changes a question passes it. A duration's own
    // unit, required and exposed, is the draft validator's
    // (`duration_unit_required`, `unsupported_duration_unit`).
  });
  return issues;
}

function identityIssues(
  draft: JsonRecord,
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  const questions = records(draft.researchQuestions);
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const testedAssets = isRecord(draft.testedAssets) ? draft.testedAssets : {};
  const slidePlan = isRecord(draft.slidePlan) ? draft.slidePlan : {};

  duplicateValueIssues(issues, records(draft.assumptions), "id", "assumptions");
  duplicateValueIssues(issues, questions, "id", "researchQuestions");
  duplicateValueIssues(
    issues,
    records(testedAssets.assets),
    "id",
    "testedAssets.assets",
  );
  duplicateValueIssues(
    issues,
    records(metricPlan.metrics),
    "id",
    "metricPlan.metrics",
  );
  duplicateValueIssues(
    issues,
    records(metricPlan.qualitativeAnalyses),
    "id",
    "metricPlan.qualitativeAnalyses",
  );
  duplicateValueIssues(
    issues,
    records(metricPlan.compositeMetrics),
    "id",
    "metricPlan.compositeMetrics",
  );
  duplicateValueIssues(
    issues,
    records(slidePlan.ordering),
    "id",
    "slidePlan.ordering",
  );
  duplicateValueIssues(
    issues,
    records(slidePlan.questionSlides),
    "questionId",
    "slidePlan.questionSlides",
  );

  records(testedAssets.assets).forEach((asset, assetIndex) =>
    duplicateValueIssues(
      issues,
      records(asset.media),
      "id",
      `testedAssets.assets[${assetIndex}].media`,
    ),
  );
  questions.forEach((question, questionIndex) =>
    responseIdentityIssues(issues, question, questionIndex),
  );

  const declaredMetricIds = new Map<string, number>();
  records(metricPlan.metrics).forEach((metric, index) => {
    if (nonBlank(metric.id) && !declaredMetricIds.has(metric.id)) {
      declaredMetricIds.set(metric.id, index);
    }
  });
  const classifierOutputOwners = new Map<string, number>();
  records(metricPlan.qualitativeAnalyses).forEach((analysis, index) => {
    const classifier = isRecord(analysis.classifier)
      ? analysis.classifier
      : null;
    if (!nonBlank(analysis.stableKey) || !nonBlank(classifier?.metricKind)) {
      return;
    }
    const outputId = openEndClassifierMetricId(
      analysis.stableKey,
      classifier.metricKind,
    );
    const declaredIndex = declaredMetricIds.get(outputId);
    const classifierIndex = classifierOutputOwners.get(outputId);
    if (declaredIndex !== undefined || classifierIndex !== undefined) {
      issue(
        issues,
        `metricPlan.qualitativeAnalyses[${index}].classifier.metricKind`,
        "accepted_identity_duplicate",
        worded("accepted_identity_duplicate", declaredIndex !== undefined
          ? `Classifier output ${outputId} collides with metricPlan.metrics[${declaredIndex}].id.`
          : `Classifier output ${outputId} collides with qualitative analysis ${classifierIndex}.`),
      );
      return;
    }
    classifierOutputOwners.set(outputId, index);
  });

  return issues;
}
// Every duplicate identity, in the draft and in each response, is judged in
// this call tree (`duplicateValueIssues` is its helper).
ruleImpl("accepted_identity_duplicate", identityIssues);

function sameStringSet(
  left: ReadonlyArray<unknown>,
  right: ReadonlyArray<unknown>,
): boolean {
  const normalized = (values: ReadonlyArray<unknown>): Array<string> =>
    values.filter(nonBlank).sort();
  const a = normalized(left);
  const b = normalized(right);
  return (
    a.length === left.length &&
    b.length === right.length &&
    a.length === b.length &&
    a.every((value, index) => value === b[index])
  );
}

function testedAssetRelationIssues(
  draft: JsonRecord,
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  const testedAssets = isRecord(draft.testedAssets) ? draft.testedAssets : {};
  const assets = records(testedAssets.assets);
  assets.forEach((asset, assetIndex) => {
    records(asset.media).forEach((media, mediaIndex) => {
      if (
        media.kind !== "image" &&
        !hasUsableNonImageAssetMediaLocator(media)
      ) {
        issue(
          issues,
          `testedAssets.assets[${assetIndex}].media[${mediaIndex}]`,
          "accepted_asset_media_locator_missing",
          repairLine("accepted_asset_media_locator_missing"),
        );
      }
    });
  });

  records(draft.researchQuestions).forEach((question, questionIndex) => {
    const questionPath = `researchQuestions[${questionIndex}]`;
    const response = isRecord(question.response) ? question.response : {};
    const rows = isRecord(response.rows) ? response.rows : null;
    const binding = isRecord(question.assetBinding)
      ? question.assetBinding
      : null;
    const bindingTypes = Array.isArray(binding?.assetTypes)
      ? binding.assetTypes
      : [];

    if (binding) {
      if (binding.mode === "none") {
        if (bindingTypes.length > 0) {
          issue(
            issues,
            `${questionPath}.assetBinding.assetTypes`,
            "asset_binding_none_has_types",
            repairLine("asset_binding_none_has_types"),
          );
        }
      } else if (bindingTypes.length === 0) {
        issue(
          issues,
          `${questionPath}.assetBinding.assetTypes`,
          "asset_binding_types_missing",
          repairLine("asset_binding_types_missing"),
        );
      }

      // The binding's row mirror (`rowSource`, and all_assets over the rows'
      // type) is derived from the response by the normaliser (S53,
      // `projectAssetBindingFromResponse`), so a mode that also owns tested
      // rows, or a mirror without them, is never written.
    }

    if (rows?.source === "tested_assets") {
      const exactType = nonBlank(rows.assetType) ? [rows.assetType] : [];
      if (
        !binding ||
        binding.mode !== "all_assets" ||
        binding.rowSource !== "tested_assets" ||
        !sameStringSet(bindingTypes, exactType)
      ) {
        issue(
          issues,
          `${questionPath}.assetBinding`,
          "asset_binding_rows_need_all_assets",
          repairLine("asset_binding_rows_need_all_assets"),
        );
      }
    }

  });

  return issues;
}

/**
 * A named-brand issue at the severity its catalog entry gives a user write:
 * `warn` is a builder advisory on the draft (the commit proceeds and the plan
 * ships; nothing else records it), every other decision an error. Owner rule:
 * a failed check degrades its one claim, never the study, and wrong data
 * never ships. So incomplete coverage of a bound requirement (a bound grid
 * with no roster, or one shorter than its brand rows, and the requirement's
 * missing brands) warns, while a requirement no question is bound to, a
 * roster naming a brand outside brandFacts, or a subject the grid does not
 * show refuses. The catalog is the one severity source.
 */
function namedBrandIssue(
  issues: Array<QuestionnaireValidationIssue>,
  path: string,
  code: RuleCode,
  message: string,
): void {
  issues.push({
    path,
    code,
    message,
    severity: issueSeverity(code),
  });
}

/** `subset` appears in `rows` in the same order (rows may hold others between). */
function isOrderedSubsequence(
  subset: ReadonlyArray<string>,
  rows: ReadonlyArray<string>,
): boolean {
  let at = 0;
  for (const label of rows) {
    if (at < subset.length && label === subset[at]) at += 1;
  }
  return at === subset.length;
}

function namedBrandEvidenceRelationIssues(
  draft: JsonRecord,
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  const evidence = isRecord(draft.studyEvidence) ? draft.studyEvidence : null;
  if (evidence?.schemaVersion !== "study-evidence-contract.v3") return issues;

  const namedRequirements = records(evidence.requirements).filter(
    (requirement) => requirement.subjectScope === "each_named_brand",
  );
  if (namedRequirements.length === 0) return issues;
  const acceptedRequirementIds = new Set(
    records(evidence.requirements)
      .map((requirement) => requirement.id)
      .filter(nonBlank),
  );

  // A subject is a roster entry by its typed identity (`isRosterEntryOf`),
  // never by its name; the roster's names are what the messages say.
  const entries = brandRoster(draft.brandFacts);
  const roster = entries.map((entry) => entry.label);
  if (roster.length === 0) {
    namedBrandIssue(
      issues,
      "brandFacts",
      "accepted_named_brand_roster_missing",
      "A v3 each_named_brand requirement requires an accepted brandFacts roster.",
    );
    return issues;
  }
  const duplicateRoster = roster.filter(
    (brand, index) => roster.indexOf(brand) !== index,
  );
  if (duplicateRoster.length > 0) {
    namedBrandIssue(
      issues,
      "brandFacts.competitors",
      "accepted_named_brand_roster_duplicate",
      `The accepted brandFacts roster repeats exact identities: ${[
        ...new Set(duplicateRoster),
      ].join(", ")}.`,
    );
  }

  const questions = records(draft.researchQuestions);
  namedRequirements.forEach((requirement, requirementIndex) => {
    if (!nonBlank(requirement.id)) return;
    const requirementPath = `studyEvidence.requirements[${requirementIndex}]`;
    const coverage = new Map<BrandRosterEntry, number>();
    let boundQuestionCount = 0;

    questions.forEach((question, questionIndex) => {
      const attribution = isRecord(question.planAttribution)
        ? question.planAttribution
        : null;
      const evidenceIds = Array.isArray(attribution?.evidenceRequirementIds)
        ? attribution.evidenceRequirementIds
        : [];
      const bindingCount = evidenceIds.filter(
        (id) => id === requirement.id,
      ).length;
      if (bindingCount === 0) return;
      boundQuestionCount += 1;

      const questionPath = `researchQuestions[${questionIndex}]`;
      // The roster entry a subject names: the entry the question's own row
      // or answer with that label is, by its typed identity.
      const response = isRecord(question.response) ? question.response : null;
      const rows = isRecord(response?.rows) ? response.rows : null;
      const fixedRows = rows?.source === "fixed" ? records(rows.rows) : [];
      const items = [...fixedRows, ...records(response?.options)];
      const rosterIdentity = (name: string): BrandRosterEntry | null => {
        const item = items.find((candidate) => candidate.label === name);
        return entries.find((entry) => isRosterEntryOf(item, entry)) ?? null;
      };
      const unknownEvidenceIds = evidenceIds
        .filter(nonBlank)
        .filter((id) => !acceptedRequirementIds.has(id));
      if (unknownEvidenceIds.length > 0) {
        namedBrandIssue(
          issues,
          `${questionPath}.planAttribution.evidenceRequirementIds`,
          "accepted_named_brand_evidence_binding_unknown",
          `Named-brand evidence bindings must resolve in studyEvidence; unknown: ${[
            ...new Set(unknownEvidenceIds),
          ].join(", ")}.`,
        );
      }
      if (bindingCount > 1) {
        namedBrandIssue(
          issues,
          `${questionPath}.planAttribution.evidenceRequirementIds`,
          "accepted_named_brand_evidence_binding_duplicate",
          `The named-brand requirement ${requirement.id} is repeated in one question's evidence binding.`,
        );
      }

      const rawEntities = question.awarenessEntities;
      // No roster: incomplete coverage, warned (the catalog decides). The
      // question's surface is still checked below, and it covers no brand.
      const hasRoster = Array.isArray(rawEntities) && rawEntities.length > 0;
      if (!hasRoster) {
        namedBrandIssue(
          issues,
          `${questionPath}.awarenessEntities`,
          "accepted_named_brand_subject_missing",
          `A question bound to ${requirement.id} must carry its exact named-brand subjects.`,
        );
      }
      const entities = hasRoster ? rawEntities.filter(nonBlank) : [];
      const uniqueEntities = [...new Set(entities)];
      if (uniqueEntities.length !== entities.length) {
        namedBrandIssue(
          issues,
          `${questionPath}.awarenessEntities`,
          "accepted_named_brand_subject_duplicate",
          "A named-brand question repeats an exact awarenessEntities identity.",
        );
      }
      const unknown = uniqueEntities.filter(
        (brand) => rosterIdentity(brand) === null,
      );
      if (unknown.length > 0) {
        namedBrandIssue(
          issues,
          `${questionPath}.awarenessEntities`,
          "accepted_named_brand_subject_unknown",
          `Named-brand subjects must come from the exact brandFacts roster; unknown: ${unknown.join(", ")}.`,
        );
      }
      const coveredIdentities = new Set(
        uniqueEntities
          .map(rosterIdentity)
          .filter((brand): brand is BrandRosterEntry => brand !== null),
      );
      coveredIdentities.forEach((brand) => {
        coverage.set(brand, (coverage.get(brand) ?? 0) + 1);
      });

      const binding = isRecord(question.assetBinding)
        ? question.assetBinding
        : null;
      const hasAssetSurface =
        nonBlank(attribution?.subjectAssetStableKey) ||
        (binding !== null && binding.mode !== "none") ||
        records(question.assetRefs).length > 0 ||
        rows?.source === "tested_assets" ||
        fixedRows.some((row) => records(row.assetRefs).length > 0);
      if (hasAssetSurface) {
        namedBrandIssue(
          issues,
          questionPath,
          "accepted_named_brand_asset_surface_conflict",
          "Named-brand research subjects cannot be represented as tested assets or asset-backed rows.",
        );
      }

      const isMatrix = MATRIX_QUESTION_TYPES.has(response?.kind as QuestionType);
      if (!isMatrix) {
        namedBrandIssue(
          issues,
          `${questionPath}.response`,
          "accepted_named_brand_matrix_surface_mismatch",
          "Each_named_brand evidence requires respondent-visible fixed matrix rows; a scalar awarenessEntities tag is insufficient.",
        );
        return;
      }

      // Every subject is a row, in row order: a subject the grid does not
      // show is wrong data. A row that is not a subject is either generic
      // ("None of these", never a brand) or a brand the roster leaves out,
      // which is incomplete coverage and warned.
      const rowLabels = fixedRows.map((row) => row.label).filter(nonBlank);
      if (
        rows?.source !== "fixed" ||
        rowLabels.length !== fixedRows.length ||
        !isOrderedSubsequence(entities, rowLabels)
      ) {
        namedBrandIssue(
          issues,
          `${questionPath}.response.rows`,
          "accepted_named_brand_matrix_surface_mismatch",
          "Named-brand matrix rows must be fixed and show every awarenessEntities subject, in order.",
        );
        return;
      }
      const unrostered = rowLabels.filter(
        (label) => rosterIdentity(label) !== null && !entities.includes(label),
      );
      if (hasRoster && unrostered.length > 0) {
        namedBrandIssue(
          issues,
          `${questionPath}.awarenessEntities`,
          "accepted_named_brand_subject_missing",
          `A question bound to ${requirement.id} must carry its exact named-brand subjects; missing: ${[
            ...new Set(unrostered),
          ].join(", ")}.`,
        );
      }
    });

    // No question carries the requirement at all: there is nothing to degrade
    // to, and report-time coverage would fail on it, so this refuses. Partial
    // coverage of a bound requirement is the advisory below.
    if (boundQuestionCount === 0) {
      namedBrandIssue(
        issues,
        requirementPath,
        "accepted_named_brand_requirement_unbound",
        `The named-brand requirement ${requirement.id} is bound to no question.`,
      );
      return;
    }

    const duplicateCoverage = entries
      .filter((entry) => (coverage.get(entry) ?? 0) > 1)
      .map((entry) => entry.label);
    if (duplicateCoverage.length > 0) {
      namedBrandIssue(
        issues,
        requirementPath,
        "accepted_named_brand_subject_coverage_duplicate",
        `The named-brand requirement covers roster identities more than once: ${duplicateCoverage.join(", ")}.`,
      );
    }
    const missingCoverage = entries
      .filter((entry) => (coverage.get(entry) ?? 0) === 0)
      .map((entry) => entry.label);
    if (missingCoverage.length > 0) {
      namedBrandIssue(
        issues,
        requirementPath,
        "accepted_named_brand_subject_coverage_missing",
        `The named-brand requirement is missing exact roster identities: ${missingCoverage.join(", ")}.`,
      );
    }
  });

  return issues;
}
// Every named-brand evidence relation is judged in this function (wave H T3).
ruleImpl("accepted_named_brand_asset_surface_conflict", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_evidence_binding_duplicate", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_evidence_binding_unknown", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_matrix_surface_mismatch", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_requirement_unbound", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_roster_duplicate", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_roster_missing", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_subject_coverage_duplicate", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_subject_coverage_missing", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_subject_duplicate", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_subject_missing", namedBrandEvidenceRelationIssues);
ruleImpl("accepted_named_brand_subject_unknown", namedBrandEvidenceRelationIssues);


/**
 * Whether a lift pair's pre half (`baseline`) and post half share one scale,
 * so the report can read the post against the pre (`paired_lift`): the same
 * question type and the same declared unit (the matrix measure's, when
 * `measureId` names one). The draft invariant reads it per minted metric; the
 * applier reads it per pair, and drops a pair a batch leaves on two scales
 * (`liftPairScaleMismatch`).
 */
export function liftHalvesShareScale(
  baseline: JsonRecord,
  owner: JsonRecord,
  measureId?: string,
): boolean {
  const unit = questionUnit(baseline, measureId);
  return baseline.type === owner.type && unit !== null && unit === questionUnit(owner, measureId);
}

/**
 * Whether a lift pair the report would read as `paired_lift` (a rating post,
 * or a matrix rating post on the measures its pre grid also has) sits on two
 * scales. A post of another type mints no paired lift, so it never mismatches.
 */
export function liftPairScaleMismatch(pre: JsonRecord, post: JsonRecord): boolean {
  if (post.type === "rating") return !liftHalvesShareScale(pre, post);
  if (post.type !== "matrix_rating" || !isRecord(post.response)) return false;
  const preMeasureIds = new Set(
    isRecord(pre.response) ? records(pre.response.measures).map((measure) => measure.id) : [],
  );
  return records(post.response.measures)
    .filter((measure) => nonBlank(measure.id) && preMeasureIds.has(measure.id))
    .some((measure) => !liftHalvesShareScale(pre, post, measure.id as string));
}

function questionUnit(question: JsonRecord, measureId?: string): string | null {
  if (!isRecord(question.response)) return null;
  if (measureId !== undefined) {
    const measure = records(question.response.measures).find(
      (candidate) => candidate.id === measureId,
    );
    return measure && nonBlank(measure.unit) ? measure.unit : null;
  }
  return nonBlank(question.response.unit) ? question.response.unit : null;
}

/** The owning scale's declared polarity (absent is the owner's default). */
function questionPolarity(
  question: JsonRecord,
  measureId?: string,
): RatingPolarity {
  if (!isRecord(question.response)) return "higher_is_better";
  const scale =
    measureId !== undefined
      ? records(question.response.measures).find(
          (candidate) => candidate.id === measureId,
        )
      : question.response;
  const declared = isRecord(scale?.anchors) ? scale.anchors.polarity : undefined;
  return RATING_POLARITIES.includes(declared as RatingPolarity)
    ? (declared as RatingPolarity)
    : "higher_is_better";
}

function canonicalMetricNormalization(
  unit: string | null,
  polarity: RatingPolarity,
): JsonRecord | null {
  if (unit === null) return null;
  const recipe = getUnitRegistryEntry(unit)?.normalization;
  if (
    !recipe ||
    recipe.method === "explicit_bounds_required" ||
    typeof recipe.min !== "number" ||
    typeof recipe.max !== "number"
  ) {
    return null;
  }
  return {
    method: recipe.method,
    min: recipe.min,
    max: recipe.max,
    direction: polarity,
    outputRange: "0_1",
  };
}

function matchesCanonicalNormalization(
  value: unknown,
  expected: JsonRecord | null,
): boolean {
  if (expected === null) return value == null;
  if (!isRecord(value)) return false;
  const keys = Object.keys(value).sort();
  const expectedKeys = Object.keys(expected).sort();
  return (
    keys.length === expectedKeys.length &&
    keys.every((key, index) => key === expectedKeys[index]) &&
    expectedKeys.every((key) => value[key] === expected[key])
  );
}

export function metricCapabilityIssues(
  draft: JsonRecord,
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  const questions = records(draft.researchQuestions);
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const metrics = records(metricPlan.metrics);
  const qualitative = records(metricPlan.qualitativeAnalyses);
  const firstQuestionById = new Map<string, JsonRecord>();
  questions.forEach((question) => {
    if (nonBlank(question.id) && !firstQuestionById.has(question.id)) {
      firstQuestionById.set(question.id, question);
    }
  });

  metrics.forEach((metric, metricIndex) => {
    const metricPath = `metricPlan.metrics[${metricIndex}]`;
    const owner = nonBlank(metric.questionId)
      ? firstQuestionById.get(metric.questionId)
      : undefined;
    if (!owner) return;

    if (metric.stableKey !== owner.stableKey) {
      issue(
        issues,
        `${metricPath}.stableKey`,
        "accepted_metric_owner_mismatch",
        worded("accepted_metric_owner_mismatch", "Metric stableKey must equal the stableKey of its questionId owner."),
      );
    }

    const source = isRecord(metric.source) ? metric.source : {};
    if (source.questionId !== metric.questionId) {
      issue(
        issues,
        `${metricPath}.source.questionId`,
        "accepted_metric_owner_mismatch",
        worded("accepted_metric_owner_mismatch", "Metric source.questionId must equal the metric questionId owner."),
      );
    }

    const ownerType = nonBlank(owner.type) ? owner.type : "";
    const capabilities = Object.prototype.hasOwnProperty.call(
      CURRENT_GENERATED_METRIC_CAPABILITIES,
      ownerType,
    )
      ? CURRENT_GENERATED_METRIC_CAPABILITIES[ownerType as QuestionType]
      : [];
    const sourceKind = nonBlank(source.kind) ? source.kind : "";
    const sourceCapabilities = capabilities.filter(
      (capability) => capability.sourceKind === sourceKind,
    );
    if (sourceCapabilities.length === 0) {
      issue(
        issues,
        `${metricPath}.source.kind`,
        "accepted_metric_source_incompatible",
        worded("accepted_metric_source_incompatible", `Question type ${ownerType || "unknown"} cannot produce a current accepted metric from source kind ${sourceKind || "unknown"}.`),
      );
    }

    if (sourceKind === "matrix_measure") {
      const measureExists =
        isRecord(owner.response) &&
        records(owner.response.measures).some(
          (measure) => measure.id === source.measureId,
        );
      if (!measureExists) {
        issue(
          issues,
          `${metricPath}.source.measureId`,
          "accepted_metric_source_incompatible",
          worded("accepted_metric_source_incompatible", "Matrix metric source.measureId must resolve on its owning matrix question."),
        );
      }
    }

    const extraction = isRecord(metric.extraction) ? metric.extraction : {};
    const method = nonBlank(extraction.method) ? extraction.method : "";
    const executableCapability = sourceCapabilities.find(
      (capability) => capability.method === method,
    );
    if (sourceCapabilities.length > 0 && !executableCapability) {
      const reserved = ["mean", "median", "qualitative_theme_package"].includes(
        method,
      );
      issue(
        issues,
        `${metricPath}.extraction.method`,
        "accepted_metric_extraction_incompatible",
        worded("accepted_metric_extraction_incompatible", reserved
          ? `Extraction method ${method} is reserved in the persisted vocabulary but has no ordinary-metric canonical-analysis dispatcher.`
          : `Extraction method ${method || "unknown"} is not executable for current generated ${ownerType} metrics from ${sourceKind}.`),
      );
    }
    if (
      executableCapability &&
      metric.metricKind !== executableCapability.metricKind
    ) {
      issue(
        issues,
        `${metricPath}.metricKind`,
        "accepted_metric_extraction_incompatible",
        repairLine("accepted_metric_extraction_incompatible", { method: String(method), metricKind: String(executableCapability.metricKind) }),
      );
    }
    if (executableCapability) {
      const expectedUnit =
        executableCapability.unit === "owner"
          ? questionUnit(
              owner,
              sourceKind === "matrix_measure" && nonBlank(source.measureId)
                ? source.measureId
                : undefined,
            )
          : null;
      const actualUnit = nonBlank(metric.unit) ? metric.unit : null;
      if (actualUnit !== expectedUnit) {
        issue(
          issues,
          `${metricPath}.unit`,
          "accepted_metric_extraction_incompatible",
          worded("accepted_metric_extraction_incompatible", executableCapability.unit === "owner"
            ? "Metric unit must equal the unit on its owning question or matrix measure."
            : `Current generated ${method} metrics do not carry a unit.`),
        );
      }

      const expectedNormalization = canonicalMetricNormalization(
        expectedUnit,
        questionPolarity(
          owner,
          sourceKind === "matrix_measure" && nonBlank(source.measureId)
            ? source.measureId
            : undefined,
        ),
      );
      if (
        !matchesCanonicalNormalization(
          metric.normalization,
          expectedNormalization,
        )
      ) {
        issue(
          issues,
          `${metricPath}.normalization`,
          "accepted_metric_normalization_incompatible",
          worded("accepted_metric_normalization_incompatible", expectedNormalization === null
            ? "This current generated metric does not carry normalization."
            : "Metric normalization must exactly match the canonical recipe for its owning unit."),
        );
      }
    }

    if (method === "paired_lift") {
      const baselineId = metric.baselineQuestionId;
      const baseline = nonBlank(baselineId)
        ? firstQuestionById.get(baselineId)
        : undefined;
      const measureId =
        sourceKind === "matrix_measure" && nonBlank(source.measureId)
          ? source.measureId
          : undefined;
      if (
        !baseline ||
        baselineId === metric.questionId ||
        !liftHalvesShareScale(baseline, owner, measureId)
      ) {
        issue(
          issues,
          `${metricPath}.baselineQuestionId`,
          "accepted_metric_baseline_incompatible",
          worded("accepted_metric_baseline_incompatible", "paired_lift requires a distinct live baseline with the same response type, unit, and matrix measure when applicable."),
        );
      }
    } else if (metric.baselineQuestionId != null) {
      issue(
        issues,
        `${metricPath}.baselineQuestionId`,
        "accepted_metric_baseline_incompatible",
        worded("accepted_metric_baseline_incompatible", "baselineQuestionId is only valid for paired_lift metrics."),
      );
    }
  });

  qualitative.forEach((analysis, analysisIndex) => {
    const analysisPath = `metricPlan.qualitativeAnalyses[${analysisIndex}]`;
    const owner = nonBlank(analysis.questionId)
      ? firstQuestionById.get(analysis.questionId)
      : undefined;
    if (!owner) return;
    if (analysis.stableKey !== owner.stableKey) {
      issue(
        issues,
        `${analysisPath}.stableKey`,
        "accepted_qualitative_owner_mismatch",
        repairLine("accepted_qualitative_owner_mismatch"),
      );
    }
    const source = isRecord(analysis.source) ? analysis.source : {};
    if (
      owner.type !== "open_end" ||
      source.kind !== "question" ||
      source.questionId !== analysis.questionId
    ) {
      issue(
        issues,
        `${analysisPath}.source`,
        "accepted_qualitative_source_incompatible",
        repairLine("accepted_qualitative_source_incompatible"),
      );
    }
  });

  const metricsByQuestionId = new Map<string, Array<string>>();
  metrics.forEach((metric) => {
    if (!nonBlank(metric.questionId) || !nonBlank(metric.id)) return;
    const owned = metricsByQuestionId.get(metric.questionId) ?? [];
    owned.push(metric.id);
    metricsByQuestionId.set(metric.questionId, owned);
  });
  const qualitativeByQuestionId = new Map<string, Array<string>>();
  qualitative.forEach((analysis) => {
    if (!nonBlank(analysis.questionId) || !nonBlank(analysis.id)) return;
    const owned = qualitativeByQuestionId.get(analysis.questionId) ?? [];
    owned.push(analysis.id);
    qualitativeByQuestionId.set(analysis.questionId, owned);
  });
  questions.forEach((question, questionIndex) => {
    if (!nonBlank(question.id) || !Array.isArray(question.metricRefs)) return;
    const expected =
      question.type === "open_end"
        ? (qualitativeByQuestionId.get(question.id) ?? [])
        : (metricsByQuestionId.get(question.id) ?? []);
    const actual = question.metricRefs.filter(nonBlank);
    const normalizedActual = [...actual].sort();
    const normalizedExpected = [...expected].sort();
    if (
      actual.length !== question.metricRefs.length ||
      normalizedActual.length !== normalizedExpected.length ||
      normalizedActual.some(
        (reference, index) => reference !== normalizedExpected[index],
      )
    ) {
      issue(
        issues,
        `researchQuestions[${questionIndex}].metricRefs`,
        "accepted_metric_refs_mismatch",
        worded("accepted_metric_refs_mismatch", question.type === "open_end"
          ? "Open-end metricRefs must exactly equal the qualitative analyses owned by this question."
          : "Question metricRefs must exactly equal the quantitative metrics owned by this question."),
      );
    }
  });

  return issues;
}

/**
 * The draft's own cross-object relations: identities, response bounds,
 * follow-up links, tested-asset and named-brand evidence relations. Part of
 * the one validity owner's issue list (Nest `questionnaireDraftIssues`), so
 * readiness, compile, the write gate and the editor's dry run read them alike.
 */
export function acceptedDraftRelationIssues(
  value: unknown,
): Array<QuestionnaireValidationIssue> {
  if (!isRecord(value)) return [];
  return [
    ...identityIssues(value),
    ...responseRelationIssues(value),
    ...followUpRelationIssues(value),
    ...testedAssetRelationIssues(value),
    ...namedBrandEvidenceRelationIssues(value),
  ];
}

/**
 * Cross-object relations for current plan-bearing accepted artifacts: the
 * draft's own (`acceptedDraftRelationIssues`) and the derived metric plan's
 * agreement with the questions (`metricCapabilityIssues`), which only code
 * writes and the write gate holds as the bundle's invariant.
 *
 * Call only in addition to the closed structural parser. This validator is
 * defensive against malformed input so composing both never throws while
 * collecting issues, but it deliberately does not duplicate field schemas.
 */
export function acceptedQuestionnaireArtifactRelationIssues(
  value: unknown,
): Array<QuestionnaireValidationIssue> {
  if (!isRecord(value)) return [];
  return [...acceptedDraftRelationIssues(value), ...metricCapabilityIssues(value)];
}

// Each rule below is decided by the function named (wave H T3, move 11).
ruleImpl('accepted_asset_media_locator_missing', testedAssetRelationIssues);
ruleImpl('asset_binding_none_has_types', testedAssetRelationIssues);
ruleImpl('asset_binding_rows_need_all_assets', testedAssetRelationIssues);
ruleImpl('asset_binding_types_missing', testedAssetRelationIssues);
ruleImpl('accepted_metric_extraction_incompatible', metricCapabilityIssues);
ruleImpl('accepted_metric_normalization_incompatible', metricCapabilityIssues);
ruleImpl('accepted_metric_owner_mismatch', metricCapabilityIssues);
ruleImpl('accepted_metric_refs_mismatch', metricCapabilityIssues);
ruleImpl('accepted_metric_source_incompatible', metricCapabilityIssues);
ruleImpl('accepted_qualitative_owner_mismatch', metricCapabilityIssues);
ruleImpl('accepted_qualitative_source_incompatible', metricCapabilityIssues);
ruleImpl('accepted_response_cardinality_invalid', responseRelationIssues);
ruleImpl('continuous_bounds_inverted', responseRelationIssues);
ruleImpl('continuous_bounds_not_registry', responseRelationIssues);
